import { SVButton, SVForm, SVPercentageInput } from '@/components/core';
import { PROGRAM_SETTINGS } from '@/constants/programSettings';
import { REGEX } from '@/constants/regex';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Box,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { UpdateSavingsInterestRateFormFields } from '@/types/companies';
import { captureException } from '@sentry/browser';

type ChangeSavingsInterestRateModalProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  currentRate?: number;
  onSubmit: (data: { rateSavingInterest: number }) => Promise<void>;
  isLoading?: boolean;
};

export default function ChangeSavingsInterestRateModal({
  open,
  setOpen,
  currentRate,
  onSubmit,
  isLoading = false,
}: ChangeSavingsInterestRateModalProps) {
  const { PROGRAM_LEVEL_SAVINGS_INTEREST_RATE } =
    PROGRAM_SETTINGS.SAVINGS_INTEREST_RATE_CONTROLS;

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingRate, setPendingRate] = useState<number>(0);

  const formMethods = useForm<UpdateSavingsInterestRateFormFields>({
    defaultValues: {
      rateSavingInterest: currentRate || 0,
    },
    mode: 'onBlur',
  });

  const handleSubmit = async (data: UpdateSavingsInterestRateFormFields) => {
    setPendingRate(data.rateSavingInterest);
    setShowConfirmation(true);
  };

  const handleConfirmChange = async () => {
    try {
      await onSubmit({ rateSavingInterest: pendingRate });
      setShowConfirmation(false);
      setOpen(false);
    } catch (error) {
      captureException(error, {
        tags: {
          component: 'ChangeSavingsInterestRateModal',
          action: 'confirm_rate_change',
        },
      });
    }
  };

  const handleCloseModal = () => {
    if (!isLoading) {
      setShowConfirmation(false);
      setOpen(false);
      formMethods.reset();
    }
  };

  return (
    <>
      <Dialog
        fullWidth
        open={open && !showConfirmation}
        onClose={handleCloseModal}>
        <SVForm<UpdateSavingsInterestRateFormFields>
          methods={formMethods}
          onSubmit={handleSubmit}>
          <DialogContent>
            <Typography variant="h6" component="h2" mb={2} fontWeight="bold">
              Change the Savings Interest Rate for Existing Bank Accounts
            </Typography>

            <Alert
              severity="warning"
              sx={{
                mb: 3,
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                color: '#856404',
              }}>
              Modifying this interest rate will change the rate on existing bank
              accounts associated with the company.
            </Alert>

            <Box sx={{ mb: 2 }}>
              <SVPercentageInput
                name="rateSavingInterest"
                label={PROGRAM_LEVEL_SAVINGS_INTEREST_RATE.LABEL}
                rules={{
                  required: true,
                  pattern: {
                    regex: REGEX.DECIMAL_NUMBER,
                    readablePatternName: 'percentage',
                  },
                  min: 0,
                  max: 100,
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <SVButton
              onClick={handleCloseModal}
              variant="tertiary"
              disabled={isLoading}>
              Cancel
            </SVButton>
            <SVButton type="submit" disabled={isLoading}>
              Confirm
            </SVButton>
          </DialogActions>
        </SVForm>
      </Dialog>

      <Dialog
        open={showConfirmation}
        onClose={() => !isLoading && setShowConfirmation(false)}>
        <DialogContent>
          <Typography variant="h6" component="h2" mb={2} fontWeight="bold">
            Changing Savings Interest Rate for Existing Accounts
          </Typography>
          <Typography sx={{ mb: 3 }}>
            Are you sure you want to change the Savings Interest Rate of{' '}
            <strong>all existing accounts</strong> to{' '}
            <strong>{pendingRate}%</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <SVButton
            onClick={() => setShowConfirmation(false)}
            variant="tertiary"
            disabled={isLoading}>
            No, Don't Change
          </SVButton>
          <SVButton
            onClick={handleConfirmChange}
            isLoading={isLoading}
            disabled={isLoading}>
            Yes, Change
          </SVButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
